import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { TYPE_META, BLANK_CONFIG } from "@/components/delivery/destinationConstants";
import { blankSecretsForEdit, buildConfig, stripBlankSecretsForPatch } from "@/components/delivery/destinationHelpers";
import { TypeSpecificFields } from "@/components/delivery/DestinationForm";
import { BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";
import { DeliveryTest } from "./DeliveryTest";
import { StepShell, setupUrl } from "./StepShell";

function ExistingDeliveries({ state, busy, onLink }) {
  const others = state.other_deliveries || [];
  if (!state.delivery_missing && (state.delivery || !others.length)) return null;
  return (
    <div className="border-2 border-amber-500 bg-amber-50 p-3 text-xs space-y-2" data-testid="setup-delivery-missing">
      <div className="font-bold">
        {state.delivery_missing ? "Their page points to a delivery that was deleted, so leads can't reach them." : "They already have a delivery. Use it, or add a new one below."}
      </div>
      {others.map((o) => (
        <div key={o.id} className="flex items-center gap-2 flex-wrap">
          <span className="flex-1 min-w-[160px]">{o.name} · {TYPE_META[o.type]?.label || o.type}{o.is_active === false ? " · paused" : ""}</span>
          <button type="button" className={BTN_WHITE} disabled={busy} onClick={() => onLink(o.id)} data-testid={`setup-delivery-use-${o.id}`}>Use this one</button>
        </div>
      ))}
      {!others.length && <div>Add a new delivery below.</div>}
    </div>
  );
}

function TypePicker({ type, onPick }) {
  return (
    <div>
      <div className="label-mono mb-2">How should leads reach their system?</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {Object.entries(TYPE_META).map(([k, m]) => (
          <button key={k} type="button" onClick={() => onPick(k)} title={m.help} data-testid={`setup-delivery-type-${k}`}
                  className={`border-2 border-black p-2 text-left text-[11px] flex items-center gap-1.5 transition-colors duration-100 ${type === k ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
            <m.Icon size={14} weight="bold" /> {m.label}
          </button>
        ))}
      </div>
      <div className="text-[11px] text-zinc-600 mt-2">{TYPE_META[type]?.help}</div>
    </div>
  );
}

export function DeliveryStep({ state, orgId, onSaved }) {
  const d = state.delivery;
  const email = state.org.contact_email || "";
  const tpl = d ? null : state.delivery_template;
  const [type, setType] = useState(d?.type || tpl?.type || "email");
  const [config, setCfg] = useState(() => (d ? { ...BLANK_CONFIG, ...blankSecretsForEdit(d.config) }
    : { ...BLANK_CONFIG, ...(tpl?.config || {}), to: email }));
  const [busy, setBusy] = useState(false);
  const setConfig = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const put = async (body, msg) => {
    setBusy(true);
    try {
      const { data } = await api.put(`${setupUrl(orgId)}/delivery`, body);
      toast.success(msg);
      onSaved(data);
    } catch (e) {
      toast.error(errText(e, "Couldn't save the delivery"));
    } finally {
      setBusy(false);
    }
  };
  const pick = (t) => { setType(t); if (t === "email" && !config.to) setConfig("to", email); };
  const save = () => put({ type, config: stripBlankSecretsForPatch(buildConfig({ type, config })) },
    d ? "Delivery saved. Send a new test lead." : "Delivery added. Now send a test lead.");
  const done = state.steps.find((s) => s.key === "delivery")?.done;
  return (
    <StepShell title="Delivery & test" testId="setup-step-delivery"
               hint="How leads reach their system. A test lead has to reach them before you can go live."
               footer={<button type="button" className={BTN_BLACK} disabled={!done} onClick={() => onSaved(state, "live")} data-testid="setup-delivery-next">Next: go live</button>}>
      <ExistingDeliveries state={state} busy={busy} onLink={(id) => put({ link_destination_id: id }, "Delivery linked. Now send a test lead.")} />
      {tpl && (
        <p className="border-2 border-black bg-sky-50 p-2 text-[11px]" data-testid="setup-delivery-copied">
          Copied the {TYPE_META[tpl.type]?.label || tpl.type} settings from {tpl.from}. Enter {state.org.name}'s own address and keys, then add the delivery.
        </p>
      )}
      <TypePicker type={type} onPick={pick} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><TypeSpecificFields type={type} config={config} setConfig={setConfig} /></div>
      {d && type !== d.type && (
        <p className="text-[11px] text-amber-700" data-testid="setup-delivery-type-change">
          Saving makes this their main delivery and pauses the current {TYPE_META[d.type]?.label || d.type} one.
        </p>
      )}
      <button type="button" className={BTN_BLACK} onClick={save} disabled={busy} data-testid="setup-delivery-save">
        {busy ? "Saving…" : d ? "Save delivery" : "Add delivery"}
      </button>
      {d && <DeliveryTest state={state} orgId={orgId} onSaved={onSaved} />}
    </StepShell>
  );
}
